/**
 * iter129 — MechanicHoursDiagnostic
 * ────────────────────────────────────────────────────────────────────
 * Why: SA reported "I entered hours for the Floor Manager every day but
 * his row in the Monthly Hours Grid shows 0." Most likely cause: those
 * entries are stored against a DIFFERENT user_id (deleted user, role
 * was 'mechanic' but later changed, etc.). This panel shows EVERY
 * mechanic_id with stored entries — joined to the users collection —
 * and offers a 1-click "Move entries to <target user>" button.
 *
 * Reads:  GET /api/super-admin/mechanic-hours/diagnose
 * Writes: POST /api/super-admin/mechanic-hours/migrate {from_id, to_id}
 */
import { useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Search, AlertTriangle, ArrowRight, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_CONFIG = {
  phantom_ids: {
    title: 'Phantom IDs',
    subtitle: 'Hours exist but no matching user — likely deleted users. Migrate to the correct active user.',
    color: 'text-rose-700 bg-rose-50 border-rose-200',
    icon: AlertTriangle,
  },
  orphan_deleted_users: {
    title: 'Soft-deleted users',
    subtitle: 'User account is deleted or inactive. Migrate hours to the replacement user.',
    color: 'text-amber-700 bg-amber-50 border-amber-200',
    icon: AlertTriangle,
  },
  excluded_role_users: {
    title: 'Wrong role',
    subtitle: 'User exists but their role is no longer mechanic/floor_manager/technician.',
    color: 'text-slate-700 bg-slate-50 border-slate-200',
    icon: AlertTriangle,
  },
  healthy_users: {
    title: 'Healthy',
    subtitle: 'Hours correctly attached to active mechanic-ish users.',
    color: 'text-emerald-700 bg-emerald-50 border-emerald-200',
    icon: CheckCircle2,
  },
  current_users_no_entries: {
    title: 'No entries yet',
    subtitle: 'These mechanic-ish users have no stored hours at all on this site.',
    color: 'text-slate-600 bg-slate-50 border-slate-200',
    icon: AlertTriangle,
  },
};

export default function MechanicHoursDiagnostic({ mechanics, onMigrated }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [migrating, setMigrating] = useState(false);
  // Per-row chosen target user_id for migration
  const [targetSelections, setTargetSelections] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/diagnose`, {
        credentials: 'include',
      });
      if (r.ok) {
        const data = await r.json();
        setReport(data);
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Diagnose call failed');
      }
    } catch (_e) {
      toast.error('Network error — try again');
    } finally {
      setLoading(false);
    }
  }, []);

  const openPanel = () => {
    setOpen(true);
    if (!report) load();
  };

  const migrate = async (fromId) => {
    const toId = targetSelections[fromId];
    if (!toId) {
      toast.error('Pick the target user first');
      return;
    }
    setMigrating(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/super-admin/mechanic-hours/migrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ from_id: fromId, to_id: toId }),
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Moved ${data.moved_rows} row(s) → ${data.to_name}`, { duration: 6000 });
        await load();
        onMigrated?.();
      } else {
        const e = await r.json().catch(() => ({}));
        toast.error(e.detail || 'Migrate call failed');
      }
    } catch (_e) {
      toast.error('Network error — try again');
    } finally {
      setMigrating(false);
    }
  };

  const renderRow = (row, key, migratable) => (
    <tr key={`${key}-${row.mechanic_id || row.user_id}`} className="border-b border-slate-100 last:border-0">
      <td className="p-2 text-[11px] font-mono text-slate-600">{row.mechanic_id || row.user_id}</td>
      <td className="p-2 text-xs text-slate-900">
        <div className="font-medium">{row.stored_name || row.user_name || row.name || '(no name)'}</div>
        <div className="text-[10px] text-slate-500">{row.user_email || row.email || ''}</div>
      </td>
      <td className="p-2 text-[10px] text-slate-600">{row.user_role || row.role || '—'}</td>
      <td className="p-2 text-xs text-right tabular-nums">{row.total_hours != null ? `${row.total_hours.toFixed(1)}h` : '—'}</td>
      <td className="p-2 text-xs text-right tabular-nums">{row.entry_count ?? '—'}</td>
      <td className="p-2 text-[10px] text-slate-500">
        {row.first_date && row.last_date ? `${row.first_date} → ${row.last_date}` : '—'}
      </td>
      <td className="p-2">
        {migratable && (
          <div className="flex items-center gap-1.5">
            <Select
              value={targetSelections[row.mechanic_id] || ''}
              onValueChange={(v) => setTargetSelections(prev => ({ ...prev, [row.mechanic_id]: v }))}
            >
              <SelectTrigger className="h-7 text-[11px] w-40" data-testid={`migrate-target-${row.mechanic_id}`}>
                <SelectValue placeholder="Target user…" />
              </SelectTrigger>
              <SelectContent>
                {(mechanics || []).map(m => (
                  <SelectItem key={m.user_id} value={m.user_id} className="text-xs">
                    {m.name || m.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              size="sm"
              variant="outline"
              disabled={migrating || !targetSelections[row.mechanic_id]}
              onClick={() => migrate(row.mechanic_id)}
              className="h-7 text-[11px]"
              data-testid={`migrate-btn-${row.mechanic_id}`}
            >
              <ArrowRight className="h-3 w-3" />
            </Button>
          </div>
        )}
      </td>
    </tr>
  );

  const renderSection = (key, rows) => {
    if (!rows || rows.length === 0) return null;
    const cfg = SECTION_CONFIG[key];
    const Icon = cfg.icon;
    const migratable = key !== 'healthy_users' && key !== 'current_users_no_entries';
    return (
      <div className={`border rounded-md ${cfg.color} mb-3`}>
        <div className="px-3 py-2 flex items-start gap-2">
          <Icon className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-semibold text-xs">{cfg.title} <span className="font-normal">· {rows.length}</span></div>
            <div className="text-[11px] opacity-80">{cfg.subtitle}</div>
          </div>
        </div>
        <div className="overflow-x-auto bg-white">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <th className="p-2 text-left">mechanic_id</th>
                <th className="p-2 text-left">Stored / current name</th>
                <th className="p-2 text-left">Role</th>
                <th className="p-2 text-right">Total</th>
                <th className="p-2 text-right">Entries</th>
                <th className="p-2 text-left">Date range</th>
                <th className="p-2 text-left">Migrate to…</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => renderRow(r, key, migratable))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={openPanel}
        className="w-full justify-between border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-900 mt-2"
        data-testid="open-hours-diagnostic"
      >
        <span className="flex items-center gap-2">
          <Search className="h-4 w-4" />
          Diagnose &amp; migrate orphan hours
        </span>
        <span className="text-[10px] opacity-80">SA · audit</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="!max-w-[95vw] w-[95vw] max-h-[90vh] overflow-y-auto" data-testid="hours-diagnostic-dialog">
          <DialogHeader>
            <DialogTitle>Mechanic Hours — Diagnose &amp; Migrate</DialogTitle>
            <DialogDescription>
              Lists every mechanic_id with stored hours, joined to your users.
              When the productivity grid shows 0 for a user you&apos;re sure has data,
              the entries are probably under a deleted / re-created user. Pick the
              target user and click → to re-bind them.
            </DialogDescription>
          </DialogHeader>

          {loading && (
            <div className="text-center py-6">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-blue-600 border-t-transparent"></div>
              <div className="text-xs text-slate-500 mt-2">Scanning mechanic_hours…</div>
            </div>
          )}

          {!loading && report && (
            <Card className="p-3 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-[11px]">
                {Object.entries(SECTION_CONFIG).map(([k, cfg]) => (
                  <div key={k} className={`p-2 rounded border ${cfg.color}`}>
                    <div className="font-semibold">{cfg.title}</div>
                    <div className="text-lg font-bold">{report.summary[`${k.replace('_ids', '_count').replace('_users', '_count').replace('current_no_entries', 'current_users_no_entries_count').replace('healthy_count', 'healthy_count')}`] ?? report[k]?.length ?? 0}</div>
                  </div>
                ))}
              </div>

              {renderSection('phantom_ids', report.phantom_ids)}
              {renderSection('orphan_deleted_users', report.orphan_deleted_users)}
              {renderSection('excluded_role_users', report.excluded_role_users)}
              {renderSection('healthy_users', report.healthy_users)}
              {renderSection('current_users_no_entries', report.current_users_no_entries)}
            </Card>
          )}

          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="hours-diagnostic-refresh">
              Refresh
            </Button>
            <Button variant="outline" size="sm" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
